import { Suspense, lazy, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { TenantProvider } from "./contexts/TenantContext";
import { SubdomainRouter } from "./components/SubdomainRouter";
import Landing from "./pages/Landing";
import SplashScreen from "./components/SplashScreen";

// Lazy load all routes except Landing for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Bookings = lazy(() => import("./pages/Bookings"));
const CreateBooking = lazy(() => import("./pages/CreateBooking"));
const AmendBooking = lazy(() => import("./pages/AmendBooking"));
const FacilityManagement = lazy(() => import("./pages/admin/FacilityManagement"));
const ActivityManagement = lazy(() => import("./pages/admin/ActivityManagement"));
const EmailTemplates = lazy(() => import("./pages/admin/EmailTemplates"));
const GuestCommunications = lazy(() => import("./pages/admin/GuestCommunications"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const GeneralSettings = lazy(() => import("./pages/admin/GeneralSettings"));
const PaymentIntegration = lazy(() => import("./pages/admin/PaymentIntegration"));
const PaymentsDashboard = lazy(() => import("./pages/admin/PaymentsDashboard"));
const OutstandingPayments = lazy(() => import("./pages/admin/OutstandingPayments"));
const Reports = lazy(() => import("./pages/admin/Reports"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const CustomerManagement = lazy(() => import("./pages/admin/CustomerManagement"));
const SystemLogs = lazy(() => import("./pages/admin/SystemLogs"));
const BookingManagement = lazy(() => import("./pages/admin/BookingManagement"));
const WardenAssignments = lazy(() => import("./pages/admin/WardenAssignments"));
const InstructorAssignments = lazy(() => import("./pages/admin/InstructorAssignments"));
const DiscountCodes = lazy(() => import("./pages/admin/DiscountCodes").then(m => ({ default: m.DiscountCodes })));
const NotFound = lazy(() => import("./pages/NotFound"));
const Profile = lazy(() => import("./pages/Profile"));
const UserSettings = lazy(() => import("./pages/Settings"));
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard"));
const WardenDashboard = lazy(() => import("./pages/WardenDashboard"));
const InstructorDashboard = lazy(() => import("./pages/InstructorDashboard"));
const BookingHistory = lazy(() => import("./pages/customer/BookingHistory"));
const BrowseFacilities = lazy(() => import("./pages/customer/BrowseFacilities"));
const BrowseActivities = lazy(() => import("./pages/customer/BrowseActivities"));
const Availability = lazy(() => import("./pages/customer/Availability"));
const PendingBookings = lazy(() => import("./pages/admin/PendingBookings"));
const AdminCustomerBooking = lazy(() => import("./pages/admin/AdminCustomerBooking"));
const AvailabilityCalendar = lazy(() => import("./pages/admin/AvailabilityCalendar"));
const PaymentPage = lazy(() => import("./pages/PaymentPage"));
const TenantManagement = lazy(() => import("./pages/admin/TenantManagement"));
const UserTenantAssignment = lazy(() => import("./pages/admin/UserTenantAssignment"));
const TenantLanding = lazy(() => import("./pages/TenantLanding"));
const JoinSiteBook = lazy(() => import("./pages/JoinSiteBook"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const PlatformPaymentSettings = lazy(() => import("./pages/admin/PlatformPaymentSettings"));
const SubscriptionConfig = lazy(() => import("./pages/admin/SubscriptionConfig"));
const PlatformDiscountCodes = lazy(() => import("./pages/admin/PlatformDiscountCodes"));
const SubscriptionPayments = lazy(() => import("./pages/admin/SubscriptionPayments"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const BookingSimplified = lazy(() => import("./pages/BookingSimplified"));
const PlatformAnalytics = lazy(() => import("./pages/admin/PlatformAnalytics"));
const SystemUsers = lazy(() => import("./pages/admin/SystemUsers"));
const PricingMatrix = lazy(() => import("./pages/admin/PricingMatrix"));
const CheckoutExtrasManagement = lazy(() => import("./pages/admin/CheckoutExtrasManagement"));
const BookingRules = lazy(() => import("./pages/admin/BookingRules"));
const RecurringBooking = lazy(() => import("./pages/admin/RecurringBooking"));
const RecurringSeries = lazy(() => import("./pages/admin/RecurringSeries"));
const Help = lazy(() => import("./pages/Help"));
const WaiverManagement = lazy(() => import("./pages/admin/WaiverManagement"));
const ReviewManagement = lazy(() => import("./pages/admin/ReviewManagement"));
const NightRatePricing = lazy(() => import("./pages/admin/NightRatePricing"));
const EmbedCalendar = lazy(() => import("./pages/EmbedCalendar"));
const ReportIssue = lazy(() => import("./pages/ReportIssue"));
const MaintenanceReports = lazy(() => import("./pages/admin/MaintenanceReports"));
const PublicReview = lazy(() => import("./pages/PublicReview"));
const RiskAssessmentRedirect = lazy(() => import("./pages/RiskAssessmentRedirect"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => {
  const [showSplash, setShowSplash] = useState(() => {
    // Only show splash on installed PWA (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         (window.navigator as any).standalone === true;
    const hasSeenSplash = sessionStorage.getItem("hasSeenSplash");
    return isStandalone && !hasSeenSplash;
  });

  const handleSplashComplete = () => {
    sessionStorage.setItem("hasSeenSplash", "true");
    setShowSplash(false);
  };

  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <TenantProvider>
          <SubdomainRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/about" element={<About />} />
                <Route path="/booking-simplified" element={<BookingSimplified />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/join" element={<JoinSiteBook />} />
                <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                <Route path="/sites/:slug" element={<TenantLanding />} />
                <Route path="/sites/:slug/risk-assessments/:activityId" element={<RiskAssessmentRedirect />} />
                <Route path="/risk-assessments/:activityId" element={<RiskAssessmentRedirect />} />
                <Route path="/embed/calendar/:slug" element={<EmbedCalendar />} />
                <Route path="/report/:slug" element={<ReportIssue />} />
                <Route path="/review/:token" element={<PublicReview />} />
                <Route path="/report/:slug/:locationCode" element={<ReportIssue />} />
                <Route path="/admin/maintenance" element={<MaintenanceReports />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/customer-dashboard" element={<CustomerDashboard />} />
                <Route path="/warden-dashboard" element={<WardenDashboard />} />
                <Route path="/instructor-dashboard" element={<InstructorDashboard />} />
                <Route path="/payments" element={<PaymentPage />} />
                <Route path="/customer/facilities" element={<BrowseFacilities />} />
                <Route path="/customer/activities" element={<BrowseActivities />} />
                <Route path="/customer/availability" element={<Availability />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={<UserSettings />} />
                <Route path="/bookings" element={<Bookings />} />
                <Route path="/booking/history" element={<BookingHistory />} />
                <Route path="/booking/create" element={<CreateBooking />} />
                <Route path="/booking/amend/:bookingId" element={<AmendBooking />} />
                <Route path="/admin/facilities" element={<FacilityManagement />} />
                <Route path="/admin/activities" element={<ActivityManagement />} />
                <Route path="/admin/users" element={<UserManagement />} />
                <Route path="/admin/customers" element={<CustomerManagement />} />
                <Route path="/admin/bookings" element={<BookingManagement />} />
                <Route path="/admin/warden-assignments" element={<WardenAssignments />} />
                <Route path="/admin/instructor-assignments" element={<InstructorAssignments />} />
                <Route path="/admin/pending-bookings" element={<PendingBookings />} />
                <Route path="/admin/customer-booking" element={<AdminCustomerBooking />} />
                <Route path="/admin/availability" element={<AvailabilityCalendar />} />
                <Route path="/admin/discount-codes" element={<DiscountCodes />} />
                <Route path="/admin/email-templates" element={<EmailTemplates />} />
                <Route path="/admin/guest-communications" element={<GuestCommunications />} />
                <Route path="/admin/settings" element={<Settings />} />
                <Route path="/admin/settings/general" element={<GeneralSettings />} />
                <Route path="/admin/payment-integration" element={<PaymentIntegration />} />
                <Route path="/admin/payments" element={<PaymentsDashboard />} />
                <Route path="/admin/payments/outstanding" element={<OutstandingPayments />} />
                <Route path="/admin/reports" element={<Reports />} />
                <Route path="/admin/logs" element={<SystemLogs />} />
                <Route path="/admin/tenants" element={<TenantManagement />} />
                <Route path="/admin/user-assignments" element={<UserTenantAssignment />} />
                <Route path="/admin/platform-payments" element={<PlatformPaymentSettings />} />
                <Route path="/admin/subscription-config" element={<SubscriptionConfig />} />
                <Route path="/admin/platform-discount-codes" element={<PlatformDiscountCodes />} />
                <Route path="/admin/subscription-payments" element={<SubscriptionPayments />} />
                <Route path="/admin/platform-analytics" element={<PlatformAnalytics />} />
                <Route path="/admin/system-users" element={<SystemUsers />} />
                <Route path="/admin/pricing-matrix" element={<PricingMatrix />} />
                <Route path="/admin/checkout-extras" element={<CheckoutExtrasManagement />} />
                <Route path="/admin/booking-rules" element={<BookingRules />} />
                <Route path="/admin/recurring-booking" element={<RecurringBooking />} />
                <Route path="/admin/recurring-series" element={<RecurringSeries />} />
                <Route path="/admin/waivers" element={<WaiverManagement />} />
                <Route path="/admin/reviews" element={<ReviewManagement />} />
                <Route path="/admin/night-rate-pricing" element={<NightRatePricing />} />
                <Route path="/help" element={<Help />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </SubdomainRouter>
        </TenantProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
